/**
 * CreateCycleForm — THE create-cycle form (owner ruling #7, 2026-09-22: "one
 * single form everywhere"). Every place that starts a cycle renders this:
 *
 *   - the cycles list (ProcessList, incl. /prioritization/cycles/new and
 *     ?create=1 from the configuration picker)   → CreateCycleDialog
 *   - Admin → Planning objects → Cycles           → CreateCycleDialog
 *   - Cycle Design (CycleSettingsPanel, create)   → CreateCycleDialog
 *
 * They used to be four forms asking four different questions (name only;
 * name + description; name + cadence + scope + workflow + strategy; name +
 * dates + scope + workflow + cadence) and landing in four different places.
 * Now one set of fields, one submit (useDegapProcessStore.createCycle →
 * create_cycle, migration 762/767), one follow-up, one destination — a host
 * may only choose the container and, where it must, override where to go.
 *
 * Fields: name*, description, dates, org scope (required when the workspace
 * has an org chart), workflow, cadence (+ frequency), strategy (when a
 * strategy module is on). No "default track owner" (removed 2026-11-03, owner
 * ruling: one person rarely owns every track; each level of a path names its
 * own owner in the Cycle Designer, falling back to the level above).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Callout, Field, Select, TextArea, TextInput } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { OrgUnitTreePicker } from '@/components/prioritization/OrgUnitTreePicker';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useEnterpriseIdentityStore } from '@/stores/prioritization/enterpriseIdentityStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useWorkflowStore } from '@/stores/prioritization/workflowStore';
import { useWorkspaceStrategies } from '@/lib/strategy/useWorkspaceStrategies';
import { workspaceStrategyOptionLabel } from '@/lib/strategy/workspaceStrategies';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useUIStore } from '@/stores/prioritization/uiStore';
import { useConfigurationsStore } from '@/stores/prioritization/configurationsStore';
import { useCycleTemplateStore } from '@/stores/prioritization/cycleTemplateStore';
import { useCycleDesignerEnabled } from '@/features/cycle-designer/hooks/useCycleDesignerEnabled';
import { createTrackWithFeedback } from '@/lib/tracks/createTrackFlow';
import { filterOrgUnitsForCycleScope, getCanonicalWorkspaceOrgRoot } from '@/lib/orgUnitTree';
import { getCurrentWorkspaceId } from '@/lib/supabaseSync';
import { getSupabaseClient } from '@/lib/supabaseClient';
import { ensureWorkspaceOrgChartIntegrity } from '@/lib/workspaceService';
import { toFriendlyDbError } from '@/lib/friendlyDbError';
import { FEATURE_FLAGS } from '@/lib/featureFlags';
import {
  getWorkspaceModules,
  isModuleFeatureAccessible,
  WORKSPACE_MODULES_UPDATED_EVENT,
} from '@/lib/workspaceModulesService';
import {
  EMPTY_CREATE_CYCLE_VALUES,
  CYCLE_FREQUENCY_OPTIONS,
  buildCreateCycleInput,
  createdCycleDestination,
  validateCreateCycleValues,
  type CreateCycleFormValues,
} from '@/lib/cycles/createCycleForm';
import {
  CREATE_CYCLE_SUBMIT_LABEL,
} from '@/lib/cycles/vocabulary';
import { CYCLE_MODE_DESCRIPTIONS, type CycleFrequency, type CycleMode } from '@/types/prioritization/degapCycle';

export interface CreateCycleFormProps {
  /** Close the container (Cancel, and after a successful create). */
  onCancel: () => void;
  /**
   * Where to go after the cycle exists. Default: the Designer's Design Paths
   * step (or the setup wizard when the Designer is off) — the same for every
   * host. Override only when the host cannot show that page.
   */
  onCreated?: (cycleId: string) => void;
  /** The configuration the cycle starts from (?config_id from the configuration picker). */
  configuration?: { id: string; name?: string | null } | null;
  /** Pre-filled name (e.g. the configuration's name). */
  initialName?: string;
}

type TemplateReadiness = 'idle' | 'checking' | 'available' | 'none';

export function CreateCycleForm({
  onCancel,
  onCreated,
  configuration,
  initialName,
}: CreateCycleFormProps) {
  const navigate = useNavigate();
  const designerEnabled = useCycleDesignerEnabled();
  const userId = useUserStore((s) => s.userId);
  const createCycle = useDegapProcessStore((s) => s.createCycle);
  const setActiveCycle = useDegapProcessStore((s) => s.setActiveCycle);
  const getDefaultTrack = useTrackStore((s) => s.getDefaultTrack);
  const workflows = useWorkflowStore((s) => s.workflows);
  const fetchWorkflows = useWorkflowStore((s) => s.fetchWorkflows);
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const getConfigurationById = useConfigurationsStore((s) => s.getConfigurationById);
  const fetchTemplates = useCycleTemplateStore((s) => s.fetchTemplates);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspaceId = currentWorkspaceId || getCurrentWorkspaceId();


  const [values, setValues] = useState<CreateCycleFormValues>(() => ({
    ...EMPTY_CREATE_CYCLE_VALUES,
    name: initialName ?? '',
  }));
  const set = <K extends keyof CreateCycleFormValues>(key: K, value: CreateCycleFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templateReadiness, setTemplateReadiness] = useState<TemplateReadiness>('idle');
  const [modules, setModules] = useState<Record<string, boolean>>({});

  // Workflows, org chart and workspace modules, once per open.
  useEffect(() => {
    void fetchWorkflows();
    if (!workspaceId) {
      void fetchOrgUnits();
      return;
    }
    void (async () => {
      try {
        const supabase = getSupabaseClient();
        if (supabase) {
          const workspace = useTenantStore.getState().getCurrentWorkspace();
          await ensureWorkspaceOrgChartIntegrity(supabase, workspaceId, workspace?.name);
        }
      } catch (err) {
        console.warn('[CreateCycleForm] ensureWorkspaceOrgChartIntegrity failed:', err);
      }
      await fetchOrgUnits({ workspaceId });
    })();
    const loadModules = () => getWorkspaceModules(workspaceId).then(({ data }) => setModules(data));
    void loadModules();
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, loadModules);
    return () => window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, loadModules);
  }, [workspaceId, fetchWorkflows, fetchOrgUnits]);

  const showStrategy = useMemo(
    () =>
      isModuleFeatureAccessible(modules, 'strategy', FEATURE_FLAGS.STRATEGY_NAV) ||
      isModuleFeatureAccessible(modules, 'strategy_kernel', FEATURE_FLAGS.STRATEGY_KERNEL_NAV),
    [modules],
  );
  // Every kind of strategy the workspace holds — classic, opportunity tree,
  // kernel, Strategy Canvas, V2MOM — so the picker is never empty just because
  // the workspace wrote its strategy in a different form.
  const strategies = useWorkspaceStrategies(showStrategy);

  const orgUnitOptions = useMemo(
    () => filterOrgUnitsForCycleScope(Array.from(orgUnits.values()), workspaceId),
    [orgUnits, workspaceId],
  );
  // Start at the workspace's org root so cycles begin at the top level.
  useEffect(() => {
    if (values.orgScopeNodeId || !workspaceId) return;
    const root = getCanonicalWorkspaceOrgRoot(orgUnitOptions, workspaceId);
    if (root) setValues((v) => ({ ...v, orgScopeNodeId: root.id }));
  }, [orgUnitOptions, values.orgScopeNodeId, workspaceId]);

  const pendingConfig = configuration?.id ? getConfigurationById(configuration.id) : null;
  const workflowRequired = pendingConfig?.workflow_required === 'explicit';
  useEffect(() => {
    if (!pendingConfig?.cycle_template_used) {
      setTemplateReadiness('idle');
      return;
    }
    setTemplateReadiness('checking');
    void fetchTemplates().then(() => {
      const available = useCycleTemplateStore.getState().templates.filter((t) => t.status !== 'archived');
      setTemplateReadiness(available.length > 0 ? 'available' : 'none');
    });
  }, [pendingConfig?.cycle_template_used, fetchTemplates]);

  const rules = { orgScopeRequired: orgUnitOptions.length > 0, workflowRequired };
  const blocker = validateCreateCycleValues(values, rules);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!userId) {
      setError('You need to be signed in to create a cycle.');
      return;
    }
    if (blocker) {
      setError(blocker);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const input = buildCreateCycleInput(values, {
        userId,
        configurationId: configuration?.id ?? null,
        strategies,
      });
      // A configuration that recommends a CycleTemplate gets the first one.
      // The server applies it once, as the cycle is made (mig 1040).
      if (pendingConfig?.cycle_template_used && templateReadiness === 'available') {
        const available = useCycleTemplateStore.getState().templates.filter((t) => t.status !== 'archived');
        if (available.length > 0) input.templateId = available[0].id;
      }
      const created = await createCycle(input);
      if (!created.ok) {
        setError(toFriendlyDbError(created.error, 'CreateCycleForm.submit'));
        return;
      }
      const cycleId = created.id;
      const notify = useUIStore.getState().addNotification;
      if (created.warnings?.length) {
        notify({
          type: 'warning',
          title: 'Cycle created, but setup is incomplete',
          message: created.warnings.join(' '),
          duration: 9000,
        });
      }
      setActiveCycle(cycleId);

      // Without the Designer nothing else makes the first track, so make it
      // here. With the Designer, Launch makes the real tracks — a placeholder
      // here would leave an extra one.
      if (!designerEnabled && !getDefaultTrack(cycleId)) {
        const scopedUnit = values.orgScopeNodeId ? orgUnits.get(values.orgScopeNodeId) ?? null : null;
        const track = await createTrackWithFeedback({
          cycleId,
          name: input.name,
          description: 'Default comparability lane for this cycle',
          scopeType: scopedUnit ? 'org_unit' : 'default',
          scopeEntityId: scopedUnit?.id ?? undefined,
          scopeLabel: scopedUnit?.displayName ?? 'Default',
        });
        if (!track.ok) {
          notify({
            type: 'warning',
            title: 'Cycle created',
            message: `The cycle was created, but its first track was not: ${track.error ?? 'unknown error'}. Add a track from the cycle page.`,
            duration: 8000,
          });
        }
      }

      setValues({ ...EMPTY_CREATE_CYCLE_VALUES });
      onCancel();
      if (onCreated) onCreated(cycleId);
      else navigate(createdCycleDestination(cycleId, designerEnabled));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The cycle could not be created. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit();
      }}
    >
      {configuration?.name && (
        <div className="space-y-1 text-sm">
          <p className="text-[var(--mustard)]">
            Starting from: <span className="font-medium">{configuration.name}</span>
          </p>
          {templateReadiness === 'checking' && (
            <p className="flex items-center gap-1.5 text-[var(--text-ter)]">
              <Loader2 className="h-3 w-3 animate-spin" /> Checking for available templates…
            </p>
          )}
          {templateReadiness === 'available' && (
            <p className="flex items-center gap-1.5 text-[var(--green)] dark:text-[var(--text-ter)]">
              <CheckCircle2 className="h-3 w-3" /> A cycle template will be applied automatically.
            </p>
          )}
          {templateReadiness === 'none' && (
            <p className="text-[var(--mustard)]">
              This configuration recommends a cycle template, but this workspace has none.{' '}
              <Link to="/prioritization/settings/templates" className="underline">Create one</Link>
            </p>
          )}
        </div>
      )}

      {workflowRequired && !values.workflowId && (
        <Callout tone="mustard" title="This configuration needs a workflow">
          Choose one below, or{' '}
          <Link to="/prioritization/settings/workflows" className="underline">set one up</Link>{' '}
          first, so governance, write-back and catchball are defined.
        </Callout>
      )}

      {error && (
        <div role="alert" className="flex items-start gap-2 border border-[var(--coral)] bg-[var(--coral-dim)] px-3 py-2 text-sm text-[var(--coral)]">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      <Field label="Cycle name" required htmlFor="create-cycle-name">
        <TextInput
          id="create-cycle-name"
          value={values.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="e.g., Q1 2026 Product Priorities"
          autoFocus
        />
      </Field>

      <Field label="Description" hint="Optional" htmlFor="create-cycle-description">
        <TextArea
          id="create-cycle-description"
          value={values.description}
          onChange={(e) => set('description', e.target.value)}
          placeholder="What is this prioritization effort for?"
          rows={3}
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Start date" hint="Optional" htmlFor="create-cycle-start">
          <TextInput id="create-cycle-start" type="date" value={values.startDate} onChange={(e) => set('startDate', e.target.value)} />
        </Field>
        <Field label="End date" hint="Optional" htmlFor="create-cycle-end">
          <TextInput id="create-cycle-end" type="date" value={values.endDate} onChange={(e) => set('endDate', e.target.value)} />
        </Field>
      </div>

      {orgUnitOptions.length > 0 && (
        <Field label="Org scope" required hint="The org-chart node this cycle starts from.">
          <OrgUnitTreePicker
            units={Array.from(orgUnits.values())}
            workspaceId={workspaceId}
            selectedId={values.orgScopeNodeId}
            onSelect={(id) => set('orgScopeNodeId', id)}
            required
          />
        </Field>
      )}

      <Field label="Workflow" required={workflowRequired} hint="Governs result resolution, catchball and session rules." htmlFor="create-cycle-workflow">
        <Select
          id="create-cycle-workflow"
          value={values.workflowId}
          onChange={(e) => set('workflowId', e.target.value)}
          options={[
            { value: '', label: 'Use workspace default' },
            ...workflows.filter((w) => w.status !== 'archived').map((w) => ({ value: w.id, label: w.name })),
          ]}
        />
      </Field>

      <fieldset className="min-w-0 border-0 p-0">
        <legend className="mb-2 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">Cadence</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {(Object.keys(CYCLE_MODE_DESCRIPTIONS) as CycleMode[]).map((mode) => {
            const cfg = CYCLE_MODE_DESCRIPTIONS[mode];
            const selected = values.cadenceMode === mode;
            return (
              <button
                key={mode}
                type="button"
                aria-pressed={selected}
                onClick={() => set('cadenceMode', mode)}
                className={`border p-3 text-left transition-colors ${
                  selected
                    ? 'border-[var(--mustard)] bg-[var(--mustard-dim)]'
                    : 'border-[var(--rule)] bg-[var(--bg)] hover:bg-[var(--hover-tint)]'
                }`}
              >
                <div className={`text-sm font-medium ${selected ? 'text-[var(--mustard)]' : 'text-[var(--text-pri)]'}`}>{cfg.label}</div>
                <div className="mt-0.5 text-xs leading-snug text-[var(--text-ter)]">{cfg.description}</div>
              </button>
            );
          })}
        </div>
      </fieldset>

      {values.cadenceMode === 'periodic' && (
        <Field label="Frequency" htmlFor="create-cycle-frequency">
          <Select
            id="create-cycle-frequency"
            value={values.frequency}
            onChange={(e) => set('frequency', e.target.value as CycleFrequency)}
            options={CYCLE_FREQUENCY_OPTIONS}
          />
        </Field>
      )}

      {showStrategy && (
        <Field
          label="Strategy"
          hint={
            strategies.length > 0
              ? 'Optional — connects this cycle to one of the workspace\'s strategies. Each is listed with its type.'
              : 'This workspace has no strategies yet. Create one under Strategies, then link it here or from the cycle\'s page.'
          }
          htmlFor="create-cycle-strategy"
        >
          <Select
            id="create-cycle-strategy"
            value={values.strategyId}
            disabled={strategies.length === 0}
            onChange={(e) => setValues((v) => ({ ...v, strategyId: e.target.value, manualStrategyName: '' }))}
            options={[
              { value: '', label: strategies.length > 0 ? 'None' : 'No strategies yet' },
              ...strategies.map((s) => ({ value: s.id, label: workspaceStrategyOptionLabel(s) })),
            ]}
          />
        </Field>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={submitting || !!blocker}>
          {submitting ? 'Creating…' : CREATE_CYCLE_SUBMIT_LABEL}
        </Button>
      </div>
    </form>
  );
}
